import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query/src/types';
import type { Request } from 'express';
import { useBloomreachCookieGetter } from '../../hooks/useBloomreachCookie';
import { sfccGet } from '../../utils/sitegenesis';
import { isServer } from '../../utils/utils';
import { bffGet } from '../bff';
import type { SearchResponse } from './model/bloomreach';

const SEARCH_TERM_MIN_LENGTH = 3;

enum QueryKey {
    BLOOMREACH_SEARCH = 'br-search',
    BRAND_SUGGEST = 'brand-suggest',
}

export type HttpParams = Record<string, string | number | boolean>[];
export type BrandSuggestions = {
    available: boolean;
    brands: string[];
};

const sortAndFlattenParams = (params: HttpParams): Array<string | number | boolean> => {
    const flattenedParams: Array<string | number | boolean> = [];
    params.sort().forEach((paramObject) => {
        Object.entries(paramObject)
            .sort()
            .forEach((entry) => {
                flattenedParams.push(...entry);
            });
    });

    return flattenedParams;
};

export const shouldQueryBloomreach = (searchTerm: string): boolean =>
    Boolean(searchTerm && searchTerm.length >= SEARCH_TERM_MIN_LENGTH);

export const getProductSearchHeaders = (req?: Partial<Request>): Record<string, string> => {
    if (isServer) {
        return {
            'x-slt-ref-url': req && req.get ? `${req.protocol}://${req.get('host')}` : '',
            'x-slt-url':
                req && req.get ? `${req.protocol}://${req.get('host')}${req.originalUrl}` : '',
        };
    }
    return {
        'x-slt-ref-url': window.document.referrer,
        'x-slt-url': window.location.href,
    };
};

export const useGetBloomreachSearch = ({
    q,
    pageSize,
    url,
    refUrl,
}: {
    q: string;
    pageSize: number;
    url: string;
    refUrl: string;
}): UseQueryResult<SearchResponse> => {
    const getBrUID2Cookie = useBloomreachCookieGetter(true);
    const httpParams: HttpParams = [{ q }, { pageSize }];
    httpParams.push({ refUrl });
    httpParams.push({ url });
    const flatSortedUrlParamsArray = sortAndFlattenParams(httpParams);

    return useQuery<SearchResponse>({
        keepPreviousData: true,
        staleTime: 1000 * 60 * 10, // 10 minutes
        queryFn: async () => {
            const brUID2 = getBrUID2Cookie();
            if (brUID2) {
                return bffGet<SearchResponse>('bloomreach/search', {
                    httpParams: [...httpParams, { brUID2 }],
                });
            }
            // if no brUID2, return empty response
            return {
                hardgood: [],
                culinary: [],
                recipe: [],
                phrases: [],
                categories: [],
            };
        },
        queryKey: [QueryKey.BLOOMREACH_SEARCH, ...flatSortedUrlParamsArray],
        enabled: !isServer && shouldQueryBloomreach(q),
    });
};

export const useGetSuggestedBrands = ({
    searchTerm,
}: {
    searchTerm: string;
}): UseQueryResult<BrandSuggestions> =>
    useQuery<BrandSuggestions>({
        queryFn: async ({ signal }) =>
            sfccGet<BrandSuggestions>('PwaSearch-GetSuggestedBrands', [{ searchTerm }], signal),
        queryKey: [QueryKey.BRAND_SUGGEST, searchTerm],
        enabled: shouldQueryBloomreach(searchTerm),
    });
